import GridContainer, { GridOffset } from "@insite/mobius/GridContainer";
import CustomModel from "../ADICustomModal";
import React, { useEffect, useState } from "react";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import translate from "@insite/client-framework/Translate";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Select from "@insite/mobius/Select";
import { css } from "styled-components";
import { CreditCardDtoCollection, updateSavedCreditCards } from "@NA/Services/SavedCreditCardsService";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import ADIColors from "@NA/Utils/ADIColors";
import { checkExpirationDate, removeZeroPrefix } from "@NA/Utils/Utility";

export interface AdiEditCreditCardModalStyles {
    editListContainer?: InjectableCss;
    editInputWapper?: InjectableCss;
    labelText?: TypographyPresentationProps;
    labledExpired?: TypographyPresentationProps;
    valueText?: TypographyPresentationProps;
    primaryButton?: ButtonPresentationProps;
    secondaryButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
}

export const adiEditCreditCardModalStyles: AdiEditCreditCardModalStyles = {
    editListContainer: {
        css: css`
            margin-top: 10px;
            > ${GridOffset} {
                > ${GridItemStyle} {
                    display: flex;
                    flex-direction: column;
                    padding-bottom: 0px;
                    padding-top: 10px;
                }
            }
        `,
    },
    labelText: {
        css: css`
            margin-bottom: 2px;
            min-height: 19px;
            font-weight: bold !important;
            line-height: normal;
            color: ${getColor("text.main")};
        `,
    },
    labledExpired: {
        css: css`
            margin-bottom: 2px;
            min-height: 19px;
            font-weight: bold !important;
            line-height: normal;
            color: ${ADIColors.danger};
        `,
    },
    valueText: {
        css: css`
            color: ${getColor("text.accent")};
            line-height: 20px;
            font-size: 14px;
        `,
    },
    editInputWapper: {
        css: css`
            display: flex;
            margin-bottom: 20px;
            width: 100%;
            gap: 15px;
        `,
    },
    buttonGridItem: {
        css: css`
            flex-direction: row !important;
            justify-content: end;
            gap: 10px;
        `,
    },
    primaryButton: {
        css: css`
            border-radius: 4px;
            min-width: 34.59%;
            width: 25% !important;
            max-width: 36% !important;
        `,
    },
    secondaryButton: {
        css: css`
            border-radius: 4px;
            min-width: 23.83%;
            width: auto !important;
            max-width: 100% !important;
        `,
    },
};

const styles = adiEditCreditCardModalStyles;

interface OwnProps {
    savedEditCard: CreditCardDtoCollection;
    closeEditCard: Function;
    getApi: Function;
    isEditCardOpen: boolean;
    filterCardType: (key: string) => string;
    expirationMonths: { key: string; value: number }[] | null | undefined;
    expirationYears: { key: number; value: number }[] | null | undefined;
    setUpdateExpMonth: Function;
    setUpdateExpYear: Function;
    updateExpMonth: string;
    updateExpYear: string;
    paginationModal: any;
    setSelectedSavedCard?: any;
}

type Props = OwnProps;

const ADIEditCreditCardModal = ({
    savedEditCard,
    isEditCardOpen,
    closeEditCard,
    getApi,
    filterCardType,
    expirationMonths,
    expirationYears,
    setUpdateExpMonth,
    setUpdateExpYear,
    updateExpMonth,
    updateExpYear,
    paginationModal,
    setSelectedSavedCard,
}: Props) => {
    const [isExpired, setIsExpired] = useState(false);
    const [ccNumber, setCCNumber] = useState("");

    useEffect(() => {
        if (updateExpYear && updateExpMonth) {
            var d = new Date();
            var x = parseInt(updateExpYear.length == 2 ? `20${updateExpYear}` : updateExpYear);
            setUpdateExpYear(x);
            setUpdateExpMonth(removeZeroPrefix(updateExpMonth));
            setIsExpired(checkExpirationDate(updateExpMonth, updateExpYear));
        }
    }, [updateExpYear, updateExpMonth]);

    // additional useEffect for setting CCNumber ---- P21 Change
    useEffect(() => {
        let ccNum = savedEditCard.cardNumber.slice(savedEditCard.cardNumber.length - 4);
        setCCNumber(ccNum);
        setSelectedSavedCard(savedEditCard);
    }, [savedEditCard]);

    const updateSavedCard = async () => {
        const isExpired = checkExpirationDate(
            parseInt(updateExpMonth).toString(),
            updateExpYear.toString().substring(2, 4),
        );

        if (!savedEditCard) return;
        if (isExpired) {
            setIsExpired(true);
            return;
        }
        try {
            const {
                cardType,
                cardNumber,
                cardToken,
                cardCVC,
                postalCode,
                isDefault,
                id,
                creditCardBankNumber,
                paymentMethodId,
                paymentTypeDesc,
                paymentTypeId,
                period,
                yearForPeriod,
            } = savedEditCard;

            // ExpiryMonth changes as per new requirement ---P21 Change
            let expiryMonth;
            if (updateExpMonth.length == 1) expiryMonth = "0" + updateExpMonth;
            else expiryMonth = updateExpMonth;

            await updateSavedCreditCards({
                uri: null,
                cardType,
                cardNumber,
                cardToken,
                //expiryMonth: parseInt(updateExpMonth).toString(),
                expiryMonth: expiryMonth,
                // expiryYear: updateExpYear.toString().substring(2, 4),
                expiryYear: parseInt(updateExpYear).toString(),
                isDefault: false,
                cardCVC: null,
                postalCode: null,
                id,
                properties: {},
                creditCardBankNumber,
                paymentMethodId,
                paymentTypeDesc,
                paymentTypeId,
                period,
                yearForPeriod,
            });

            const card: CreditCardDtoCollection = {
                cardType,
                cardNumber,
                cardToken,
                cardCVC,
                postalCode,
                expiryMonth: parseInt(updateExpMonth).toString(),
                //expiryYear: updateExpYear.toString().substring(2, 4),
                expiryYear: parseInt(updateExpYear).toString(),
                isDefault,
                id,
                uri: "",
                properties: {},
                creditCardBankNumber: "",
                paymentMethodId: "",
                paymentTypeDesc: "",
                paymentTypeId: "",
                period: "",
                yearForPeriod: "",
            };

            if (card && setSelectedSavedCard) {
                // setSelectedSavedCard(card);
                savedEditCard.expiryMonth = card.expiryMonth;
                savedEditCard.expiryYear = card.expiryYear;
            }

            closeEditCard();
            getApi(paginationModal?.currentPage, 4, savedEditCard);
        } catch (error) {
            throw error;
        }
    };
    const isValidYear = (year: string) => {
        const currentYear = new Date().getFullYear();
        const parsedYear = parseInt(year, 10);
        return !isNaN(parsedYear) && parsedYear >= currentYear;
    };
    return (
        <CustomModel
            headline={"Edit Credit Card"}
            isOpen={isEditCardOpen}
            errorStyle={false}
            handleClose={closeEditCard}
        >
            <>
                {savedEditCard && (
                    <GridContainer {...styles.editListContainer}>
                        <GridItem width={[12, 12, 12, 4, 4]}>
                            <Typography variant="headerSecondary" {...styles.labelText}>
                                {translate("Credit Card Type *")}
                            </Typography>
                            <Typography variant="p" {...styles.valueText}>
                                {filterCardType(savedEditCard.cardType)}
                            </Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 12, 6, 6]}>
                            <Typography variant="headerSecondary" {...styles.labelText}>
                                {translate("Credit Card Number *")}
                            </Typography>
                            <Typography variant="p" {...styles.valueText}>
                                {/* //----P21 Change */}
                                **** {ccNumber}
                            </Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 12, 8, 8]}>
                            <Typography
                                variant="headerSecondary"
                                {...(isExpired ? styles.labledExpired : styles.labelText)}
                            >
                                {translate("Expiration Date *")}
                            </Typography>
                            <StyledWrapper {...styles.editInputWapper}>
                                <Select
                                    border="rounded"
                                    value={updateExpMonth}
                                    onChange={e => setUpdateExpMonth(e.target.value)}
                                >
                                    {expirationMonths &&
                                        expirationMonths.length > 0 &&
                                        expirationMonths.map(item => (
                                            <option key={item.key} value={item.value}>
                                                {item.value}
                                            </option>
                                        ))}
                                </Select>
                                <Select
                                    border="rounded"
                                    value={updateExpYear}
                                    onChange={e => setUpdateExpYear(e.target.value)}
                                >
                                    {!isValidYear(updateExpYear) && <option value=""></option>}
                                    {expirationYears &&
                                        expirationYears.map(item => (
                                            <option key={item.key} value={item.key}>
                                                {item.value}
                                            </option>
                                        ))}
                                </Select>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem width={12} {...styles.buttonGridItem}>
                            <Button onClick={() => closeEditCard()} variant="secondary" {...styles.secondaryButton}>
                                {translate("Cancel")}
                            </Button>
                            <Button onClick={() => updateSavedCard()} variant="primary" {...styles.primaryButton}>
                                {translate("Save")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                )}
            </>
        </CustomModel>
    );
};

export default ADIEditCreditCardModal;
